import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Pause, Search, Quote, Check, Copy, AlertTriangle, Download, FolderOpen, Loader2 } from 'lucide-react';
import { IS_WEB } from '../lib/platform';
import type { Meeting, SpeakerSegment } from '../types';
import { SpeakerAssignMenu } from './SpeakerAssignMenu';
import { displaySpeaker } from '../features/recording/speakerLabels';
import { t } from '../lib/i18n';

interface TranscriptPlayerProps {
  transcript: SpeakerSegment[];
  durationSeconds: number;
  isDarkMode: boolean;
  onInsertQuoteToNote: (speaker: string, text: string, timestamp: string) => void;
  /** Kaydın file:// ya da S3 (süreli imzalı) adresi. Yoksa oynatıcı devre dışı görünür. */
  audioUrl?: string;
  /** Kaydın ham dosya yolu — "klasörde göster" için (yalnızca yerel kayıtlar). */
  audioFilePath?: string;
  /** Ses ve transkript S3'ten yükleniyor. */
  isLoadingMedia?: boolean;
  /** S3'ten yükleme hatası. */
  mediaError?: string | null;
  /** Ses açılamadı — S3 adresinin süresi dolmuş olabilir, yenisini iste. */
  onAudioError?: () => void;
  /** Toplantı S3'te mi (boş durum metni için). */
  isRemote?: boolean;
  /** Konuşmacı etiketi → eşlenen kişi. */
  speakerMap?: Meeting['speakerMap'];
  /** Verilirse konuşmacı adına tıklayınca kişi eşleme menüsü açılır. */
  onAssignSpeaker?: (speakerLabel: string, personId: string | null) => Promise<void>;
}

const PLAYBACK_RATES = [1, 1.25, 1.5, 2];

function formatTime(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return '00:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export const TranscriptPlayer: React.FC<TranscriptPlayerProps> = ({
  transcript,
  durationSeconds,
  isDarkMode,
  onInsertQuoteToNote,
  audioUrl,
  audioFilePath,
  isLoadingMedia = false,
  mediaError = null,
  onAudioError,
  isRemote = false,
  speakerMap,
  onAssignSpeaker
}) => {
  /** Kişi eşleme menüsünün açık olduğu segment. */
  const [assigningSegmentId, setAssigningSegmentId] = useState<string | null>(null);
  const displayName = (label: string) => speakerMap?.[label]?.name ?? displaySpeaker(label);

  // Gerçek <audio> öğesi. Önceki sürümde oynatma tamamen taklitti: bir
  // setInterval sayacı ilerliyordu ama hiçbir ses çalmıyordu ve kaydedilen
  // dosya (meeting.audioUrl) hiç kullanılmıyordu.
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [audioError, setAudioError] = useState<string | null>(null);
  // Süreyi dosyanın kendisinden öğren; meta veri yüklenene kadar kayıt
  // sırasında ölçülen süreyi kullan.
  const [loadedDuration, setLoadedDuration] = useState<number | null>(null);

  const [transcriptSearch, setTranscriptSearch] = useState('');
  const [activeSpeakerFilter, setActiveSpeakerFilter] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const effectiveDuration = loadedDuration ?? durationSeconds;
  const hasAudio = Boolean(audioUrl) && !audioError;

  // Farklı bir toplantıya geçildiğinde oynatıcıyı sıfırla.
  useEffect(() => {
    setIsPlaying(false);
    setCurrentSeconds(0);
    setAudioError(null);
    setLoadedDuration(null);
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = playbackRate;
  }, [playbackRate]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !hasAudio) return;

    if (audio.paused) {
      audio.play().catch(() => setAudioError(t('Ses dosyası oynatılamadı.', 'Could not play the audio file.')));
    } else {
      audio.pause();
    }
  }, [hasAudio]);

  const seekTo = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio || !hasAudio) return;
    audio.currentTime = Math.min(Math.max(0, seconds), effectiveDuration || 0);
    setCurrentSeconds(audio.currentTime);
  }, [hasAudio, effectiveDuration]);

  /** Transkript satırının zaman damgasına tıklayınca sesi oraya sar. */
  const jumpToTime = useCallback((seconds: number) => {
    if (!hasAudio) return;
    seekTo(seconds);
    audioRef.current?.play().catch(() => setAudioError(t('Ses dosyası oynatılamadı.', 'Could not play the audio file.')));
  }, [hasAudio, seekTo]);

  const handleCopyTranscriptLine = (id: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    }, (err) => console.error('[clipboard] kopyalanamadı:', err));
  };

  const speakers = Array.from(new Set(transcript.map(t => t.speaker)));
  const search = transcriptSearch.toLocaleLowerCase('tr');
  const filteredTranscript = transcript.filter(t => {
    const matchesSpeaker = activeSpeakerFilter === 'all' || t.speaker === activeSpeakerFilter;
    const matchesSearch =
      !search ||
      t.text.toLocaleLowerCase('tr').includes(search) ||
      displayName(t.speaker).toLocaleLowerCase('tr').includes(search);
    return matchesSpeaker && matchesSearch;
  });

  return (
    <div className={`h-full flex flex-col select-none transition-colors ${
      isDarkMode ? 'bg-[#111113] text-neutral-200' : 'bg-neutral-50 text-neutral-800'
    }`}>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            // MediaRecorder'ın ürettiği webm'lerde süre bazen Infinity gelir —
            // o durumda kayıt sırasında ölçülen süreye güven.
            if (Number.isFinite(d) && d > 0) setLoadedDuration(d);
            e.currentTarget.playbackRate = playbackRate;
          }}
          onTimeUpdate={(e) => setCurrentSeconds(e.currentTarget.currentTime)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onError={() => {
            setAudioError(t('Kayıt dosyası bulunamadı veya açılamadı.', 'The recording file could not be found or opened.'));
            onAudioError?.();
          }}
        />
      )}

      {/* Oynatıcı çubuğu */}
      <div className={`p-5 border-b space-y-3 shrink-0 ${
        isDarkMode ? 'border-[#3A3A3E] bg-[#19191C]' : 'border-neutral-200 bg-white'
      }`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              id="btn-play-pause-audio"
              onClick={togglePlay}
              disabled={!hasAudio}
              aria-label={isPlaying ? t('Duraklat', 'Pause') : t('Oynat', 'Play')}
              className="w-8 h-8 rounded-full bg-[#1DA4BA] hover:bg-[#3AC8E0] disabled:bg-neutral-600 disabled:cursor-not-allowed text-[#F0EEE6] flex items-center justify-center shadow-xs transition-transform active:scale-95 shrink-0"
            >
              {isPlaying ? <Pause className="w-4 h-4 fill-white" /> : <Play className="w-4 h-4 fill-white ml-0.5" />}
            </button>
            <div className="min-w-0">
              <div className="text-[13.5px] font-semibold text-neutral-100 truncate">
                {hasAudio ? t('Ses Kaydı', 'Audio recording') : t('Ses kaydı yok', 'No audio recording')}
              </div>
              <div className="text-[12px] text-neutral-400 font-mono">
                {formatTime(currentSeconds)} / {formatTime(effectiveDuration)}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {audioFilePath && (
              <button
                onClick={() => void window.zabit.revealRecording(audioFilePath).catch(() => {})}
                // Web'de "klasör" yok: kayıt dosya olarak iner.
                title={IS_WEB ? t('Kaydı indir', 'Download recording') : t('Kaydı klasörde göster', 'Show recording in folder')}
                className="p-1.5 rounded text-neutral-400 hover:text-[#46CBE2] transition-colors"
              >
                {IS_WEB ? <Download className="w-4 h-4" /> : <FolderOpen className="w-4 h-4" />}
              </button>
            )}
            <div className="flex items-center gap-1.5 text-[12px]">
              {PLAYBACK_RATES.map((rate) => (
                <button
                  key={rate}
                  onClick={() => setPlaybackRate(rate)}
                  disabled={!hasAudio}
                  className={`px-2 py-1 rounded font-mono transition-colors disabled:opacity-40 ${
                    playbackRate === rate
                      ? 'bg-[#1DA4BA] text-[#F0EEE6] font-bold'
                      : isDarkMode ? 'bg-[#1F1F23] text-neutral-400 hover:text-[#F0EEE6]' : 'bg-neutral-200 text-neutral-600'
                  }`}
                >
                  {rate}x
                </button>
              ))}
            </div>
          </div>
        </div>

        <input
          type="range"
          min={0}
          max={effectiveDuration || 0}
          step={0.1}
          value={currentSeconds}
          disabled={!hasAudio}
          onChange={(e) => seekTo(Number(e.target.value))}
          aria-label={t('Ses konumu', 'Audio position')}
          className="w-full h-1.5 bg-neutral-700/50 rounded-lg appearance-none cursor-pointer accent-[#3AC8E0] disabled:cursor-not-allowed disabled:opacity-40"
        />

        {audioError && (
          <p className="text-[12px] text-amber-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {audioError}
          </p>
        )}
        {mediaError && (
          <p className="text-[12px] text-amber-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {mediaError}
          </p>
        )}
        {isLoadingMedia && (
          <p className="text-[12px] text-neutral-500 flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {t('Ses ve transkript yükleniyor…', 'Loading audio and transcript…')}
          </p>
        )}
        {!audioUrl && !isLoadingMedia && !mediaError && (
          <p className="text-[12px] text-neutral-500">
            {isRemote
              ? t('Bu toplantının ses kaydı yok.', 'This meeting has no audio recording.')
              : IS_WEB
                ? t(
                    'Ses kaydı başka bir cihazda ya da henüz buluta yüklenmedi — şimdilik yalnızca transkript mevcut.',
                    'The audio is on another device or not uploaded yet — only the transcript is available for now.'
                  )
                : t(
                    'Bu toplantı bu bilgisayarda kaydedilmedi — yalnızca transkript mevcut.',
                    'This meeting wasn’t recorded on this computer — only the transcript is available.'
                  )}
          </p>
        )}
      </div>

      {/* Arama + konuşmacı filtresi */}
      <div className={`p-3.5 border-b space-y-2 shrink-0 ${
        isDarkMode ? 'border-[#3A3A3E] bg-[#19191C]' : 'border-neutral-200 bg-white'
      }`}>
        <div className={`flex items-center gap-2.5 px-1.5 py-2 border-b text-[13.5px] ${
          isDarkMode ? 'border-[#3F3F43] text-neutral-300' : 'border-neutral-200 text-neutral-800'
        }`}>
          <Search className="w-4 h-4 text-neutral-400 shrink-0" />
          <input
            type="text"
            placeholder={t('Transkript içinde kelime ara...', 'Search the transcript…')}
            value={transcriptSearch}
            onChange={(e) => setTranscriptSearch(e.target.value)}
            className="bg-transparent border-none outline-none w-full placeholder:text-neutral-500 text-[13.5px]"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveSpeakerFilter('all')}
            className={`px-2.5 py-1 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap ${
              activeSpeakerFilter === 'all'
                ? 'bg-[#1DA4BA] text-[#F0EEE6] font-semibold'
                : isDarkMode ? 'bg-[#1F1F23] text-neutral-400 hover:text-[#F0EEE6]' : 'bg-neutral-200 text-neutral-700'
            }`}
          >
            {t('Tüm Konuşmacılar', 'All speakers')}
          </button>
          {speakers.map((sp) => (
            <button
              key={sp}
              onClick={() => setActiveSpeakerFilter(sp)}
              className={`px-2.5 py-1 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap ${
                activeSpeakerFilter === sp
                  ? 'bg-[#1DA4BA] text-[#F0EEE6] font-semibold'
                  : isDarkMode ? 'bg-[#1F1F23] text-neutral-400 hover:text-[#F0EEE6]' : 'bg-neutral-200 text-neutral-700'
              }`}
            >
              {displayName(sp)}
            </button>
          ))}
        </div>
      </div>

      {/* Transkript */}
      <div className={`flex-1 overflow-y-auto p-5 divide-y no-scrollbar ${isDarkMode ? 'divide-[#36363A]' : 'divide-neutral-100'}`}>
        {transcript.length === 0 && !isLoadingMedia && (
          <p className="text-[13px] text-neutral-500 italic py-4">
            {t('Bu kayıtta transkript yok.', 'This recording has no transcript.')}
          </p>
        )}

        {filteredTranscript.map((segment, idx) => {
          const nextSeconds = filteredTranscript[idx + 1]?.seconds ?? Infinity;
          const isCurrent = hasAudio && currentSeconds >= segment.seconds && currentSeconds < nextSeconds;
          return (
            <div
              key={segment.id}
              className={`group py-3.5 pl-3.5 border-l-2 transition-colors ${
                isCurrent ? 'border-[#3AC8E0]' : 'border-transparent'
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2.5 relative">
                  <div className="w-5 h-5 rounded-full bg-neutral-700 text-[#F0EEE6] text-[11px] font-bold flex items-center justify-center">
                    {displayName(segment.speaker)[0]}
                  </div>
                  {onAssignSpeaker ? (
                    <button
                      onClick={() => setAssigningSegmentId(assigningSegmentId === segment.id ? null : segment.id)}
                      title={t('Bu konuşmacıyı bir kişiyle eşle', 'Match this speaker to a person')}
                      className={`text-[13px] font-semibold hover:text-[#46CBE2] hover:underline underline-offset-2 decoration-dotted ${
                        isDarkMode ? 'text-neutral-200' : 'text-neutral-800'
                      }`}
                    >
                      {displayName(segment.speaker)}
                    </button>
                  ) : (
                    <span className={`text-[13px] font-semibold ${
                      isDarkMode ? 'text-neutral-200' : 'text-neutral-800'
                    }`}>{displayName(segment.speaker)}</span>
                  )}
                  {speakerMap?.[segment.speaker] && (
                    <span className="text-[11px] text-neutral-500">({displaySpeaker(segment.speaker)})</span>
                  )}
                  {onAssignSpeaker && assigningSegmentId === segment.id && (
                    <div className="absolute left-0 top-6">
                      <SpeakerAssignMenu
                        speakerLabel={segment.speaker}
                        currentPersonId={speakerMap?.[segment.speaker]?.personId}
                        isDarkMode={isDarkMode}
                        onAssign={(personId) => onAssignSpeaker(segment.speaker, personId)}
                        onClose={() => setAssigningSegmentId(null)}
                      />
                    </div>
                  )}
                </div>

                <button
                  onClick={() => jumpToTime(segment.seconds)}
                  disabled={!hasAudio}
                  title={hasAudio ? t('Bu ana atla', 'Jump to this moment') : t('Ses kaydı yok', 'No audio recording')}
                  className={`text-[12px] font-mono px-2 py-1 rounded transition-colors disabled:cursor-default enabled:hover:bg-[#1DA4BA] enabled:hover:text-[#F0EEE6] ${
                    isCurrent ? 'text-[#46CBE2] font-bold' : 'text-neutral-500'
                  }`}
                >
                  {segment.timestamp}
                </button>
              </div>

              <p className={`text-[13.5px] leading-relaxed ${
                isDarkMode ? 'text-neutral-300' : 'text-neutral-700'
              }`}>
                {segment.text}
              </p>

              <div className="mt-1.5 flex items-center justify-end gap-2.5 text-[12px] opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleCopyTranscriptLine(segment.id, segment.text)}
                  className="text-neutral-400 hover:text-[#F0EEE6] flex items-center gap-1.5 transition-colors px-2 py-1 rounded hover:bg-neutral-700/30"
                >
                  {copiedId === segment.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedId === segment.id ? t('Kopyalandı', 'Copied') : t('Kopyala', 'Copy')}</span>
                </button>
                <button
                  onClick={() => onInsertQuoteToNote(displayName(segment.speaker), segment.text, segment.timestamp)}
                  className="text-[#46CBE2] hover:text-[#7BDAEA] font-semibold flex items-center gap-1.5 transition-colors px-2 py-1 rounded hover:bg-[#3AC8E0]/10"
                >
                  <Quote className="w-3.5 h-3.5" />
                  <span>{t('Notuma Alıntı Ekle', 'Add quote to my note')}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
